import { useEffect, useState } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type SecondaryCategoryConfig } from '@webapp/platform/organization-management/entities/SecondaryCategoryConfig'
import messages from './messages'
import { useQueryReportAccessSecondaryCategories } from '../../models/useQueryReportAccessSecondaryCategories'

const PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 400

export interface IUseReportAccessSecondaryCategoryListPresenterParams {
  primaryCategoryId: number
  selectedSecondaryCategoryId: number | undefined
  isAllSecondaryCategoriesSelected: boolean
  onSelect: (config: SecondaryCategoryConfig) => void
  onToggleSelectAll: () => void
}

export const useReportAccessSecondaryCategoryListPresenter = ({
  primaryCategoryId,
  selectedSecondaryCategoryId,
  isAllSecondaryCategoriesSelected,
  onSelect,
  onToggleSelectAll,
}: IUseReportAccessSecondaryCategoryListPresenterParams) => {
  const intl = useProcurifyIntl()

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim())
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // A new PrimaryCategory re-scopes the list — reset search and paging
  useEffect(() => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setPage(1)
  }, [primaryCategoryId])

  const { secondaryCategoryConfigs, pagination, isFetching, error } =
    useQueryReportAccessSecondaryCategories(primaryCategoryId, {
      page,
      pageSize: PAGE_SIZE,
      search: debouncedSearchTerm || undefined,
    })

  // Auto-select-first rule (BHV-007): on fresh list load with no active
  // selection, promote the first config to the Feature Set
  useEffect(() => {
    if (
      !isFetching &&
      !error &&
      selectedSecondaryCategoryId === undefined &&
      !isAllSecondaryCategoriesSelected &&
      secondaryCategoryConfigs.length > 0
    ) {
      onSelect(secondaryCategoryConfigs[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFetching,
    error,
    selectedSecondaryCategoryId,
    isAllSecondaryCategoriesSelected,
    secondaryCategoryConfigs.length,
  ])

  useEffect(() => {
    if (error) {
      toast.error({
        title: intl.formatMessage(messages.fetchErrorToastTitle),
        children:
          getMessageFromError(error) ??
          intl.formatMessage(messages.fetchErrorToastMessage),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const handleSearchChange = (term: string) => {
    setSearchTerm(term)
    setPage(1)
  }

  const handleRowClick = (config: SecondaryCategoryConfig) => {
    // Re-clicking the active row is a no-op (BHV-008)
    if (
      !isAllSecondaryCategoriesSelected &&
      config.id === selectedSecondaryCategoryId
    ) {
      return
    }

    onSelect(config)
  }

  return {
    data: {
      secondaryCategoryConfigs,
      currentPage: pagination?.page || page,
      numPages: pagination?.numPages || 1,
      searchTerm,
    },
    states: {
      isLoading: isFetching,
      // Hide the "Select all" bar while a search term is present (BHV-009)
      isSelectAllBarVisible: searchTerm.trim().length === 0,
    },
    methods: {
      onSearchChange: handleSearchChange,
      onPageChange: setPage,
      onRowClick: handleRowClick,
      onToggleSelectAll,
    },
  }
}
